<script setup lang="ts">
import { computed } from 'vue';
import TagIconComponent from './TagIcon.vue';
import {
    TagIcon,
    Color,
} from '@precon-tools-suite-by-shared/precon-graphql-types';
import { useColorClass } from '@precon-tools-suite-by-shared/shared-colors-ui';

const props = withDefaults(
    defineProps<{
        modelValue: TagIcon | null | undefined;
        options?: TagIcon[];
        cols?: number;
        size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl';
        color?: Color;
    }>(),
    {
        cols: 4,
        options: () => [
            TagIcon.Hashtag,
            TagIcon.Asterisk,
            TagIcon.Code,
            TagIcon.Box,
            TagIcon.Flag,
            TagIcon.Bookmark,
            TagIcon.Star,
            TagIcon.Heart,
            TagIcon.Cloud,
            TagIcon.Database,
            TagIcon.Folder,
            TagIcon.File,
            TagIcon.Clock,
            TagIcon.Calendar,
            TagIcon.Megaphone,
            TagIcon.Verified,
        ],
        size: 'lg',
        color: Color.Blue,
    }
);

const emit = defineEmits<{
    (e: 'update:modelValue', value: TagIcon): void;
}>();

const optionGrid = computed(() => {
    const options = props.options;
    const cols = props.cols;
    const result: TagIcon[][] = [];
    for (let rowStart = 0; rowStart < options.length; rowStart += cols) {
        result.push(options.slice(rowStart, rowStart + cols));
    }
    return result;
});

const colorClass = useColorClass(() => props.color);

const selectedOptionClass = computed(() => {
    return [
        colorClass.bg('500'),
        colorClass('hover:bg', '400'),
        colorClass.text('100'),
    ];
});

const otherOptionClass = computed(() => {
    return [colorClass('hover:bg', '200')];
});

function selectIconHandler(icon: TagIcon) {
    emit('update:modelValue', icon);
}
</script>

<template>
    <div class="tag-icon-select">
        <div class="tag-icon-select-option-rows">
            <template v-for="(optionRow, row) of optionGrid" :key="`${row}`">
                <div class="tag-icon-select-option-row">
                    <template v-for="option of optionRow" :key="option">
                        <div
                            class="tag-icon-select-option"
                            :class="
                                option == modelValue
                                    ? selectedOptionClass
                                    : otherOptionClass
                            "
                        >
                            <TagIconComponent
                                :icon="option"
                                :size="size"
                                @click="selectIconHandler(option)"
                            />
                        </div>
                    </template>
                </div>
            </template>
        </div>
    </div>
</template>

<style lang="scss">
.tag-icon-select {
    .tag-icon-select-option-row {
        display: flex;
    }
    .tag-icon-select-option {
        padding: 0.5rem;
        border-radius: 50%;
        cursor: pointer;
    }
}
</style>
